import { useAppSelector, useAppTranslation } from "@/utils";
import { CloseCircleFilled, InfoCircleFilled, InfoCircleOutlined } from "@payping/icons";
import {
  Button,
  Checkbox,
  Flex,
  Input,
  LegacyForm,
  Modal,
  Select,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { FC, useState } from "react";
import { Trans } from "@payping/utils/localeUtils";
import { bnplMerchantApis } from "@payping/api";
import { Link } from "react-router-dom";
import refund from "@/refund/routes/constants";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

interface CancelOrderProps {
  open: boolean;
  onCancle: () => void;
  trackingCode: string;
  onFinish: () => void;
}
interface FormValues {
  reason: number;
  description: string;
}
const BnplListDetailsPageCancelOrderModal: FC<CancelOrderProps> = ({ onCancle, open, trackingCode, onFinish }) => {
  const { t } = useAppTranslation();
  const [confirm, setConfrim] = useState(false);
  const orderDetails = useAppSelector((state) => state.bnplMerchant.orderDetail);
  const [acceptRules, setAccessRules] = useState(false);

  const [formData, setFormData] = useState<{
    reason: string;
    description: string;
  }>({
    description: "",
    reason: "",
  });

  const [form] = LegacyForm.useForm<FormValues>();

  const reasonsList = t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.form.reason.options", {
    returnObjects: true,
  }) as string[];

  const [cancelOrderState, cancelOrder] = useMessagedAsyncFn(
    async (reason: string, description: string) => {
      await bnplMerchantApis.orderCancelMerchant({ trackingCode, description, reason });
      onCancle();
      onFinish();
    },
    [trackingCode, onCancle, onFinish],
    { loading: false },
    { success: t("shop.bnpl.orderDetails.successCancelOrder") },
  );

  const [getRefundState, getRefund] = useMessagedAsyncFn(
    () => bnplMerchantApis.getOrderRefundInfo(trackingCode),
    [trackingCode],
  );

  const hnadleSubmitCancelOrder = async (data: FormValues) => {
    // need to refund

    if (orderDetails?.cancelNeedsRefund) {
      setFormData({
        description: data.description ?? "",
        reason: reasonsList[data.reason],
      });
      getRefund();
      return;
    }

    // do not need to refund
    await cancelOrder(reasonsList[data.reason], data.description ?? "");
    onCancle();
    onFinish();
  };

  const handleCancelWithRefund = async () => {
    await cancelOrder(formData.reason, formData.description);
    onCancle();
    onFinish();
  };

  if (!open) {
    return null;
  }

  if (acceptRules && getRefundState.value?.refundIsActive === true) {
    return (
      <Modal
        open={open}
        onCancel={onCancle}
        title={
          <Flex align="center" gap={"middle"}>
            <InfoCircleOutlined className="tw:text-color-warning" width={20} />
            {t("shop.bnpl.orderDetails.actions.cancelOrder.refundModal.title")}
          </Flex>
        }
        footer={
          <Flex align="center" justify="end" gap={"middle"}>
            <Button type="default" onClick={() => onCancle()} disabled={cancelOrderState.loading}>
              {t("shop.bnpl.orderDetails.actions.cancelOrder.refundModal.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                handleCancelWithRefund();
              }}
              loading={cancelOrderState.loading}
            >
              {t("shop.bnpl.orderDetails.actions.cancelOrder.refundModal.confirm")}
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap={"middle"}>
          <Typography.Text>
            <Trans
              i18nKey={"shop.bnpl.orderDetails.actions.cancelOrder.refundModal.content"}
              values={{
                amount: `${formatLocalizedNumber({ value: getRefundState.value?.prePaymentRefundableAmount ?? 0, options: { maximumFractionDigits: 2 } })} ${t("common.tomans")}`,
                cardNo: orderDetails?.prePaymentCardNumber ?? "",
                fullName: `${orderDetails?.consumerInfo.firstName ?? ""} ${orderDetails?.consumerInfo.lastName ?? ""}`,
              }}
              components={{
                span: <span dir="ltr" className="tw:inline-block" />,
              }}
            />
          </Typography.Text>
        </Flex>
      </Modal>
    );
  }

  // not active refund
  if (acceptRules && getRefundState.value?.refundIsActive === false) {
    return (
      <Modal
        open={open}
        onCancel={onCancle}
        title={
          <Flex align="center" gap={"middle"}>
            <CloseCircleFilled className="tw:text-color-error" width={20} />
            {t("shop.bnpl.orderDetails.actions.cancelOrder.refundDisabledModal.title")}
          </Flex>
        }
        footer={
          <Flex align="center" justify="end" gap={"middle"}>
            <Button type="default" onClick={() => onCancle()}>
              {t("shop.bnpl.orderDetails.actions.cancelOrder.refundDisabledModal.cancel")}
            </Button>
            <Link to={refund.init.url}>
              <Button type="primary">
                {t("shop.bnpl.orderDetails.actions.cancelOrder.refundDisabledModal.confirm")}
              </Button>
            </Link>
          </Flex>
        }
      >
        <Flex vertical gap={"middle"}>
          <Typography.Text>
            {t("shop.bnpl.orderDetails.actions.cancelOrder.refundDisabledModal.content")}
          </Typography.Text>
        </Flex>
      </Modal>
    );
  }

  if (acceptRules)
    return (
      <Modal
        open={open}
        onCancel={onCancle}
        title={
          <Flex align="center" gap={"middle"}>
            {t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.title")}
          </Flex>
        }
        footer={
          <Flex align="center" justify="end" gap={"middle"}>
            <Button
              type="default"
              onClick={() => onCancle()}
              disabled={cancelOrderState.loading || getRefundState.loading}
            >
              {t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.cancel")}
            </Button>
            <Button
              disabled={!confirm}
              type="primary"
              onClick={() => form.submit()}
              loading={cancelOrderState.loading || getRefundState.loading}
            >
              {t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.submit")}
            </Button>
          </Flex>
        }
      >
        <Flex vertical>
          <LegacyForm layout="vertical" form={form} onFinish={hnadleSubmitCancelOrder}>
            <LegacyForm.Item<FormValues>
              rules={[{ required: true }]}
              required
              label={t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.form.reason.label")}
              name={"reason"}
            >
              <Select options={reasonsList.map((item, index) => ({ value: index, label: item }))} />
            </LegacyForm.Item>
            <LegacyForm.Item<FormValues>
              rules={[{ max: 180 }]}
              name="description"
              label={t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.form.description.label")}
            >
              <Input.TextArea maxLength={180} rows={3} />
            </LegacyForm.Item>
          </LegacyForm>
          <Typography.Text>{t("shop.bnpl.orderDetails.actions.cancelOrder.requestModal.form.content")}</Typography.Text>
        </Flex>
      </Modal>
    );

  return (
    <Modal
      open={open}
      onCancel={onCancle}
      title={
        <Flex align="center" gap={"middle"}>
          <InfoCircleFilled className="tw:text-color-primary" width={20} />
          {t("shop.bnpl.orderDetails.actions.cancelOrder.rulesModal.title")}
        </Flex>
      }
      footer={
        <Flex align="center" justify="end" gap={"middle"}>
          <Button type="default" onClick={() => onCancle()}>
            {t("shop.bnpl.orderDetails.actions.cancelOrder.rulesModal.cancel")}
          </Button>
          <Button disabled={!confirm} type="primary" onClick={() => setAccessRules(true)}>
            {t("shop.bnpl.orderDetails.actions.cancelOrder.rulesModal.submit")}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={"middle"}>
        <ul>
          {(
            t("shop.bnpl.orderDetails.actions.cancelOrder.rulesModal.content", { returnObjects: true }) as string[]
          ).map((_, index) => (
            <li key={index}>
              <Typography.Text>
                {
                  <Trans
                    i18nKey={`shop.bnpl.orderDetails.actions.cancelOrder.rulesModal.content.${index}`}
                    components={{ strong: <strong /> }}
                  />
                }
              </Typography.Text>
            </li>
          ))}
        </ul>
        <Checkbox
          checked={confirm}
          onChange={(e: any) => {
            setConfrim(Boolean(e?.target?.checked));
          }}
        >
          {t("shop.bnpl.orderDetails.actions.cancelOrder.rulesModal.confirm")}
        </Checkbox>
      </Flex>
    </Modal>
  );
};

export default BnplListDetailsPageCancelOrderModal;
